// ===========================================
// Award Achievement Modal Component
// ===========================================
// Modal for awarding achievements to students

import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import { EmojiEvents, Star } from '@mui/icons-material';
import {
  AchievementDefinition,
  AwardAchievementData,
  AwardSpecialAchievementData,
  ACHIEVEMENT_ICONS,
} from '../../types/gamification.types';
import AchievementBadge from './AchievementBadge';

interface AwardAchievementModalProps {
  open: boolean;
  onClose: () => void;
  studentName: string;
  definitions: AchievementDefinition[];
  onAwardExisting: (data: AwardAchievementData) => Promise<void>;
  onAwardSpecial: (data: AwardSpecialAchievementData) => Promise<void>;
  isAwarding?: boolean;
  error?: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const BADGE_ICONS = Object.entries(ACHIEVEMENT_ICONS);
const BADGE_COLORS = [
  '#FFD700', // Gold
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#9C27B0', // Purple
  '#FF5722', // Orange
  '#E91E63', // Pink
  '#00BCD4', // Cyan
  '#795548', // Brown
];

export default function AwardAchievementModal({
  open,
  onClose,
  studentName,
  definitions,
  onAwardExisting,
  onAwardSpecial,
  isAwarding = false,
  error,
}: AwardAchievementModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [selectedDefinition, setSelectedDefinition] = useState<string>('');

  // Special achievement form state
  const [specialName, setSpecialName] = useState('');
  const [specialDescription, setSpecialDescription] = useState('');
  const [specialIcon, setSpecialIcon] = useState('star');
  const [specialColor, setSpecialColor] = useState('#FFD700');

  const handleClose = () => {
    setSelectedDefinition('');
    setSpecialName('');
    setSpecialDescription('');
    setSpecialIcon('star');
    setSpecialColor('#FFD700');
    setTabValue(0);
    onClose();
  };

  const handleAward = async () => {
    if (tabValue === 0 && selectedDefinition) {
      await onAwardExisting({ achievementDefinitionId: selectedDefinition });
      handleClose();
    } else if (tabValue === 1 && specialName && specialDescription) {
      await onAwardSpecial({
        name: specialName,
        description: specialDescription,
        badgeIcon: specialIcon,
        badgeColor: specialColor,
      });
      handleClose();
    }
  };

  const canAward =
    (tabValue === 0 && selectedDefinition) ||
    (tabValue === 1 && specialName && specialDescription);

  const activeDefinitions = definitions.filter((d) => d.isActive);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmojiEvents sx={{ color: 'warning.main' }} />
          Award Achievement to {studentName}
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Existing Achievement" />
          <Tab label="Special Achievement" />
        </Tabs>

        {/* Tab 1: Select Existing Achievement */}
        <TabPanel value={tabValue} index={0}>
          {activeDefinitions.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              No achievement definitions available. Create some in the admin settings first.
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {activeDefinitions.map((def) => (
                <Grid item xs={6} sm={4} md={3} key={def.id}>
                  <Box
                    onClick={() => setSelectedDefinition(def.id)}
                    sx={{
                      cursor: 'pointer',
                      p: 1,
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor:
                        selectedDefinition === def.id ? 'primary.main' : 'transparent',
                      backgroundColor:
                        selectedDefinition === def.id ? 'primary.light' : 'background.default',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <AchievementBadge achievement={def} size="small" showName />
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        {/* Tab 2: Create Special Achievement */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={2}>
            {/* Name */}
            <Grid item xs={12}>
              <TextField
                label="Achievement Name"
                value={specialName}
                onChange={(e) => setSpecialName(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Performance Star"
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={specialDescription}
                onChange={(e) => setSpecialDescription(e.target.value)}
                fullWidth
                required
                multiline
                rows={2}
                placeholder="e.g., Outstanding performance at the spring recital"
              />
            </Grid>

            {/* Icon Selection */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Badge Icon
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BADGE_ICONS.map(([name, emoji]) => (
                  <Box
                    key={name}
                    onClick={() => setSpecialIcon(name)}
                    sx={{
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      border: '2px solid',
                      borderColor: specialIcon === name ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      fontSize: '1.25rem',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    {emoji}
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Color Selection */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Badge Color
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {BADGE_COLORS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setSpecialColor(color)}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: color,
                      border: '3px solid',
                      borderColor: specialColor === color ? 'primary.main' : 'transparent',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease',
                      '&:hover': { transform: 'scale(1.1)' },
                    }}
                  />
                ))}
              </Box>
            </Grid>

            {/* Preview */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Preview
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <AchievementBadge
                  achievement={{
                    id: 'preview',
                    schoolId: '',
                    type: 'SPECIAL',
                    name: specialName || 'Achievement Name',
                    description: specialDescription || 'Achievement description',
                    threshold: 0,
                    badgeIcon: specialIcon,
                    badgeColor: specialColor,
                    isActive: true,
                    isDefault: false,
                    sortOrder: 0,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  }}
                  size="large"
                  showName
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isAwarding}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAward}
          disabled={!canAward || isAwarding}
          startIcon={isAwarding ? <CircularProgress size={20} /> : <Star />}
        >
          {isAwarding ? 'Awarding...' : 'Award Achievement'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
